import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Users, Expand, MessageSquare } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useConversationStats } from '@/hooks/stats'
import { useMemo } from 'react'
import { useDateRange } from '@/hooks/useDateRange'
import { CalendarDatePicker } from '../ui/calendar-date-picker'

const userActivityConfig = {
  conversations: {
    label: 'Total Conversations',
    color: 'hsl(var(--chart-1))',
    icon: Users,
  },
  messages: {
    label: 'Total Messages',
    color: 'hsl(var(--chart-2))',
    icon: MessageSquare,
  },
} satisfies ChartConfig

const UserActivityChart = () => {
  const { dateRange, setDateRange, formattedRange } = useDateRange()

  const { data: conversationStats, isLoading: isConversationStatsLoading } = useConversationStats({
    startDate: formattedRange.startDate,
    endDate: formattedRange.endDate,
    enabled: Boolean(dateRange.from && dateRange.to),
  })

  const userChartData = useMemo(() => {
    if (!conversationStats?.periodStats.dailyStats) return []

    const userStats = new Map<
      string,
      {
        userId: string
        conversations: number
        messages: number
      }
    >()

    Object.values(conversationStats.periodStats.dailyStats).forEach((dayStats) => {
      dayStats.conversationDetails.forEach((conv) => {
        const userStat = userStats.get(conv.userId) || {
          userId: conv.userId,
          conversations: 0,
          messages: 0,
        }

        userStat.conversations++
        userStat.messages += conv.messageCount
        userStats.set(conv.userId, userStat)
      })
    })

    return Array.from(userStats.values())
  }, [conversationStats])

  return (
    <Dialog>
      <Card className="w-full min-w-0 lg:w-auto">
        <CardHeader className="flex flex-col items-start justify-between space-y-0 pb-2">
          <div className="flex items-center justify-between w-full">
            <CardTitle>User Activity</CardTitle>
            <DialogTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 data-[state=open]:bg-muted" aria-label="Open">
                <Expand className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </div>
          <div className="flex max-sm:flex-col items-center  justify-between w-full">
            <CardDescription>User engagement trends</CardDescription>
            <CalendarDatePicker variant="outline" date={dateRange} onDateSelect={setDateRange} />
          </div>
        </CardHeader>
        <CardContent className="min-w-0">
          {isConversationStatsLoading ? (
            <Skeleton className="h-[18.75rem] w-full" />
          ) : (
            <ChartContainer config={userActivityConfig} className="h-[18.75rem] w-full min-w-0 overflow-hidden">
              <LineChart accessibilityLayer data={userChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="userId"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => value.slice(0, 8)}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Line
                  type="monotone"
                  dataKey="conversations"
                  stroke="var(--color-conversations)"
                  strokeWidth={2}
                  dot={true}
                />
                <Line type="monotone" dataKey="messages" stroke="var(--color-messages)" strokeWidth={2} dot={true} />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
      <DialogContent className="sm:max-w-[90%] max-h-[80vh] overflow-auto">
        <DialogHeader>
          <CardHeader className="p-0">
            <DialogTitle asChild>
              <CardTitle>User Activity</CardTitle>
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex max-sm:flex-col justify-between">
                <CardDescription>User engagement trends</CardDescription>
                <CalendarDatePicker variant="outline" date={dateRange} onDateSelect={setDateRange} />
              </div>
            </DialogDescription>
          </CardHeader>
        </DialogHeader>
        {isConversationStatsLoading ? (
          <Skeleton className="h-[37.5rem] w-full" />
        ) : (
          <ChartContainer config={userActivityConfig} className="h-[37.5rem] w-full">
            <LineChart data={userChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="userId"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => value.slice(0, 8)}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Line
                type="monotone"
                dataKey="conversations"
                stroke="var(--color-conversations)"
                strokeWidth={2}
                dot={true}
              />
            </LineChart>
          </ChartContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UserActivityChart
